/**
 * Turnkey session maintenance — the pure decision core behind the
 * per-platform `TurnkeySessionSteward` components.
 *
 * Why this exists: the Turnkey wallet kit only refreshes its session via an
 * in-memory timer 60s before expiry (tab must be open), and on page load it
 * silently purges an already-expired session WITHOUT firing
 * `onSessionExpired`. The Morfi backend session lives independently (7-day
 * cookie / rolling RN bearer token), so a user can return fully signed in
 * while their signing session is dead — every transaction then fails until
 * they log out and back in.
 *
 * The steward closes that gap with two actions decided here:
 *   - `refresh` — the session is valid but ageing: proactively re-mint it so
 *     any visit extends it (rolling, like the Morfi session).
 *   - `reconcile-logout` — a Turnkey-credential user is Morfi-authed with no
 *     valid session: an expired session cannot be silently re-minted
 *     (re-auth requires OAuth/OTP), so fan out the existing session-expired
 *     logout and let the user land in a clean signed-out state.
 *
 * Everything here is pure over an input snapshot — the platform components
 * own the IO (kit `getSession()`/`refreshSession()`, adapter appState/netInfo,
 * the throttle stamp) and the guards' sourcing.
 */

import { SESSION_REFRESH_MIN_AGE_MS, SESSION_REFRESH_THROTTLE_MS } from '../session-policy.js';

/** The subset of the kit's `Session` the decision needs. */
export interface TurnkeySessionSnapshot {
  /** Expiry as epoch SECONDS (the kit's `Session.expiry`). */
  readonly expiry: number;
  /**
   * TTL captured when the session was stored (kit `Session.expirationSeconds`,
   * stringly and optional — derived from the JWT at parse time).
   */
  readonly expirationSeconds?: string | undefined;
}

export type TurnkeySessionAction = 'none' | 'refresh' | 'reconcile-logout';

export interface SessionMaintenanceInput {
  /** Fresh read from the kit's `getSession()` — NEVER the per-tab React state
   *  (`useTurnkey().session` does not re-read storage after init, so another
   *  tab's refresh/login would be invisible and cause a false logout). */
  readonly session: TurnkeySessionSnapshot | null | undefined;
  readonly nowMs: number;
  /** `adapter.netInfo.current().online` — offline states are never acted on
   *  (a refresh would fail → the kit logs itself out; a reconcile logout
   *  would fire on a captive-portal blip). */
  readonly online: boolean;
  /** Kit `clientState === 'ready'` — before init settles, session state is
   *  meaningless. */
  readonly clientReady: boolean;
  /** Morfi auth status `'authed'` AND `user.turnkeySubOrgId !== null` — only
   *  Turnkey-credential users need a signing session; external-wallet users
   *  never reconcile. */
  readonly authedWithSubOrg: boolean;
  /** `peekFreshLogin() !== null` — a Turnkey login is buffered but the Morfi
   *  session isn't minted yet. */
  readonly freshLoginPending: boolean;
  /** `isCompletingOauth()` — an OAuth token exchange is buffered/in flight. */
  readonly completingOauth: boolean;
  /** A Turnkey modal is open (login/OTP in progress). */
  readonly modalActive: boolean;
  /** Last proactive-refresh stamp (adapter storage), or null if never. */
  readonly lastRefreshAtMs: number | null;
}

/** When the TTL is unknown (missing/NaN `expirationSeconds`), refresh when
 *  less than this remains. */
export const REFRESH_FALLBACK_REMAINING_MS = 48 * 60 * 60 * 1000;
/** Settle delay before a reconcile logout fires — all guards re-run against a
 *  fresh `getSession()` after this, so transient init/login states clear. */
export const RECONCILE_SETTLE_MS = 2_000;

/** True iff the snapshot exists and has not expired. */
export function isTurnkeySessionValid(
  session: TurnkeySessionSnapshot | null | undefined,
  nowMs: number,
): boolean {
  return session != null && session.expiry * 1000 > nowMs;
}

/**
 * Decide the steward's next action for one trigger (kit-ready, foreground,
 * connectivity-restored). See the module doc for the two actions; `'none'`
 * covers every guarded or healthy state.
 */
export function decideTurnkeySessionAction(input: SessionMaintenanceInput): TurnkeySessionAction {
  if (!input.clientReady || !input.online) return 'none';
  // Any in-progress auth flow owns the session lifecycle — never interfere.
  if (input.freshLoginPending || input.completingOauth || input.modalActive) return 'none';

  if (isTurnkeySessionValid(input.session, input.nowMs)) {
    return shouldRefresh(input) ? 'refresh' : 'none';
  }

  // No valid session. Only a Morfi-authed Turnkey-credential user is in the
  // broken mixed state; everyone else (anon, external-wallet) is healthy.
  return input.authedWithSubOrg ? 'reconcile-logout' : 'none';
}

function shouldRefresh(input: SessionMaintenanceInput): boolean {
  // `session` is non-null and valid here (checked by the caller).
  const session = input.session as TurnkeySessionSnapshot;
  if (
    input.lastRefreshAtMs !== null &&
    input.nowMs - input.lastRefreshAtMs < SESSION_REFRESH_THROTTLE_MS
  ) {
    return false;
  }

  const remainingMs = session.expiry * 1000 - input.nowMs;
  const ttlMs = Number(session.expirationSeconds) * 1000;
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    return remainingMs < REFRESH_FALLBACK_REMAINING_MS;
  }

  const ageMs = ttlMs - remainingMs;
  // Age ≥ min(1 day, ttl/2): the shared policy, capped so a short-lived
  // session still rolls before it dies.
  const threshold = Math.min(SESSION_REFRESH_MIN_AGE_MS, ttlMs / 2);
  return ageMs >= threshold;
}
